import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  DragEvent,
} from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';

export interface DocumentTag {
  id: string;
  name: string;
}

export interface DocumentUploadData {
  title: string;
  description?: string;
  file: File;
  tags: string[];
}

export interface DocumentUploadPageProps {
  availableTags: DocumentTag[];
  isUploading?: boolean;
  isNative?: boolean;
  onBack?: () => void;
  onUpload: (data: DocumentUploadData) => void;
  maxFileSizeMB?: number;
  acceptedFileTypes?: string[];
}

const TAG_COLORS: Record<string, string> = {
  funding: '#4CAF50',
  find_care: '#FF9800',
  power_of_attorney: '#9C27B0',
};

const getTagColor = (tagId: string): string => {
  return TAG_COLORS[tagId] || '#757575';
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentUploadPage = ({
  availableTags,
  isUploading,
  isNative = false,
  onBack,
  onUpload,
  maxFileSizeMB = 10,
  acceptedFileTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'],
}: DocumentUploadPageProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateAndSetFile = useCallback(
    (file: File) => {
      const maxBytes = maxFileSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        setFileError(`File size must be less than ${maxFileSizeMB}MB`);
        return false;
      }

      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!acceptedFileTypes.includes(extension)) {
        setFileError(`Accepted file types: ${acceptedFileTypes.join(', ')}`);
        return false;
      }

      setFileError(null);
      setSelectedFile(file);
      // Auto-populate title from filename (without extension)
      if (!title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setTitle(nameWithoutExt);
      }
      return true;
    },
    [maxFileSizeMB, acceptedFileTypes, title],
  );

  const handleFileSelect = useCallback(() => {
    setFileError(null);
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      validateAndSetFile(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [validateAndSetFile],
  );

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        validateAndSetFile(file);
      }
    },
    [validateAndSetFile],
  );

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTags((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId],
    );
  }, []);

  const handleChangeFile = useCallback(() => {
    setSelectedFile(null);
    setTitle('');
    setDescription('');
    setSelectedTags([]);
    setFileError(null);
    setTitleError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    setTitleError(null);

    if (!title.trim()) {
      setTitleError('Title is required');
      return;
    }

    if (!selectedFile) {
      setFileError('Please select a file to upload');
      return;
    }

    onUpload({
      title: title.trim(),
      description: description.trim() || undefined,
      file: selectedFile,
      tags: selectedTags,
    });
  }, [title, description, selectedFile, selectedTags, onUpload]);

  const canSubmit = title.trim() && selectedFile && !isUploading;

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title={selectedFile ? 'Document Details' : 'Upload Document'}
            description={
              selectedFile
                ? 'Add details for your document'
                : 'Select a file to upload'
            }
          />
        }
        contentSlot={
          <Stack spacing={3}>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type='file'
              accept={acceptedFileTypes.join(',')}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {!selectedFile ? (
              // Step 1: File selection
              <Stack spacing={2}>
                {isNative ? (
                  // Native: Button only
                  <Button
                    variant='contained'
                    startIcon={<Icon icon='upload' fontSize={18} />}
                    onClick={handleFileSelect}
                    disabled={isUploading}
                    fullWidth
                    size='large'
                    sx={{ py: 3 }}
                  >
                    Select File
                  </Button>
                ) : (
                  // Web: Drag and drop area
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleFileSelect}
                    sx={{
                      p: 6,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: isDragging ? 'primary.main' : 'divider',
                      bgcolor: isDragging
                        ? alpha(theme.palette.primary.main, 0.05)
                        : 'grey.50',
                      cursor: isUploading ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': isUploading
                        ? {}
                        : {
                            borderColor: 'primary.light',
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                    }}
                  >
                    <Stack
                      spacing={2}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <Icon
                        icon='cloud-arrow-up'
                        fontSize={56}
                        color={
                          isDragging
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary
                        }
                      />
                      <Typography
                        variant='h6'
                        color={isDragging ? 'primary' : 'text.secondary'}
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        {isDragging
                          ? 'Drop file here'
                          : 'Drag and drop a file here'}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        or
                      </Typography>
                      <Button
                        variant='contained'
                        disabled={isUploading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileSelect();
                        }}
                      >
                        Browse Files
                      </Button>
                    </Stack>
                  </Box>
                )}

                {fileError && (
                  <Typography
                    variant='body2'
                    color='error'
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    {fileError}
                  </Typography>
                )}

                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Accepted: {acceptedFileTypes.join(', ')} • Max size:{' '}
                  {maxFileSizeMB}MB
                </Typography>
              </Stack>
            ) : (
              // Step 2: Form fields (after file is selected)
              <Stack spacing={3}>
                {/* Selected file display */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <Stack
                    direction='row'
                    spacing={1.5}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Icon
                      icon='file-contract'
                      fontSize={28}
                      color={theme.palette.primary.main}
                    />
                    <Stack
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant='body1'
                        noWrap
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {selectedFile.name}
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Stack>
                    {!isUploading && (
                      <Button size='small' onClick={handleChangeFile}>
                        Change
                      </Button>
                    )}
                  </Stack>
                </Box>

                {/* Title field */}
                <TextField
                  label='Title'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={!!titleError}
                  helperText={titleError}
                  required
                  fullWidth
                  disabled={isUploading}
                  autoFocus
                />

                {/* Description field */}
                <TextField
                  label='Description'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={isUploading}
                  placeholder='Optional notes about this document'
                />

                {/* Tags */}
                {availableTags.length > 0 && (
                  <Stack spacing={1}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Tags (optional)
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      useFlexGap
                      sx={{
                        flexWrap: 'wrap',
                      }}
                    >
                      {availableTags.map((tag) => {
                        const isSelected = selectedTags.includes(tag.id);
                        return (
                          <Chip
                            key={tag.id}
                            label={tag.name}
                            onClick={() => handleTagToggle(tag.id)}
                            disabled={isUploading}
                            variant={isSelected ? 'filled' : 'outlined'}
                            sx={{
                              borderColor: getTagColor(tag.id),
                              color: isSelected ? 'white' : getTagColor(tag.id),
                              bgcolor: isSelected
                                ? getTagColor(tag.id)
                                : 'transparent',
                              '&:hover': {
                                bgcolor: isSelected
                                  ? getTagColor(tag.id)
                                  : `${getTagColor(tag.id)}20`,
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                )}

                {/* Submit button */}
                <Button
                  variant='contained'
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  fullWidth
                  size='large'
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={18} color='inherit' />
                    ) : (
                      <Icon icon='upload' fontSize={18} />
                    )
                  }
                >
                  {isUploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </Stack>
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

DocumentUploadPage.displayName = 'DocumentUploadPage';
export default DocumentUploadPage;
